import { GLOBAL_FILTER_PRESETS, LOCATION_FILTER_PRESETS, PresetLocations } from './data';
import { FilterPreset } from './types';

const stateMap: { [key: string]: string } = {
  // Texas variations
  tx: 'tx',
  texas: 'tx',
  tex: 'tx',

  // Florida variations
  fl: 'fl',
  florida: 'fl',
  fla: 'fl',

  // Illinois variations
  il: 'il',
  illinois: 'il',
  ill: 'il',

  // Washington variations
  wa: 'wa',
  washington: 'wa',
  wash: 'wa',

  // California variations
  ca: 'ca',
  california: 'ca',
  calif: 'ca',
  cal: 'ca',

  // New York variations
  ny: 'ny',
  'new york': 'ny',
  newyork: 'ny',

  // North Carolina variations
  nc: 'nc',
  'north carolina': 'nc',
  northcarolina: 'nc',
  'n carolina': 'nc',
  'n. carolina': 'nc',
};

// Helper function to normalize state names to handle variations
const normalizeStateName = (state: string): string => {
  const normalized = state.toLowerCase().trim().replace(/\./g, '');
  return stateMap[normalized] || normalized;
};

// Helper function to parse location string and extract city/state
const parseLocation = (location: string): { city: string; state?: string } => {
  // Remove common suffixes like "usa", "united states", etc.
  const cleaned = location
    .toLowerCase()
    .replace(/,?\s*(usa|united states|us|america)$/i, '')
    .trim();

  // Split by comma to separate city and state
  const parts = cleaned.split(',').map((part) => part.trim());

  if (parts.length >= 2) {
    const city = parts[0];
    const state = normalizeStateName(parts[1]);
    return { city, state };
  }

  // If no comma, treat as city only
  return { city: cleaned };
};

// helper function to get presets for a specific location
export const getPresetsForLocation = (location: string): FilterPreset[] => {
  if (!location) return [];

  const normalizedLocation = location.toLowerCase().trim();
  const { city, state } = parseLocation(normalizedLocation);

  // Find matching location enum
  const locationEnum = Object.values(PresetLocations).find((loc) => {
    const enumLocation = loc.toLowerCase();
    const { city: enumCity, state: enumState } = parseLocation(enumLocation);

    // Check if city matches
    if (enumCity !== city) return false;

    // If both have states, they must match
    if (state && enumState) {
      return normalizeStateName(state) === normalizeStateName(enumState);
    }

    // If one has state and other doesn't, consider it a match if cities match
    // This handles cases where input might be "lake travis" vs "lake travis, tx"
    return true;
  }) as PresetLocations;

  if (!locationEnum) {
    return []; // Return only global presets if location not found
  }

  const locationPresets = LOCATION_FILTER_PRESETS[locationEnum] || [];

  return [...locationPresets, ...GLOBAL_FILTER_PRESETS];
};
